import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SafeMarkdown } from './safe-markdown';

// Full content to render in the read-only "View" dialog for one version.
// Deliberately its own shape (not e.g. `KbDocVersionDetail` from
// support-agent-api.ts) — this drawer is shared with the Instructions panel,
// which has no doc/tags/status concept, so it stays decoupled from any one
// caller's API types. `status`/`tags` are optional since the Instructions
// panel's version history has neither.
export interface VersionHistoryViewContent {
  title: string;
  body: string;
  updatedBy: string;
  updatedAt: string;
  status?: string;
  tags?: string[];
}

export interface VersionHistoryEntry {
  key: string;
  updatedBy: string;
  updatedAt: string;
  summary?: React.ReactNode;
  onRestore?: () => void;
  // When set, the Restore button renders disabled with this text in a
  // tooltip (used for KB doc versions — see support-agent-api.ts, the
  // server's version snapshots don't expose `body`).
  restoreDisabledReason?: string;
  // When set, a "View" button renders on this card. Clicking it opens a
  // read-only dialog with the full version content (this entry's summary
  // only ever carries a title/snippet, never the full body — see
  // `getKbDocVersion` in support-agent-api.ts). A rejection surfaces as an
  // inline error in the dialog rather than a crash.
  onView?: () => Promise<VersionHistoryViewContent>;
}

interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  entries: VersionHistoryEntry[];
  emptyMessage?: string;
}

/** Shared version-history drawer for both the KB editor and the Instructions panel. */
export const VersionHistoryDrawer: React.FC<VersionHistoryDrawerProps> = ({
  open,
  onClose,
  title,
  entries,
  emptyMessage,
}) => {
  // ── "View" dialog state ──────────────────────────────────────────────
  // `viewEntry` doubles as the dialog's open flag (non-null = open) so the
  // dialog's Restore button can reuse `viewEntry.onRestore` directly —
  // there's no separate restore implementation here, just a call-through
  // to whatever the caller (knowledge-base-panel.tsx) already wired up.
  const [viewEntry, setViewEntry] = useState<VersionHistoryEntry | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewContent, setViewContent] = useState<VersionHistoryViewContent | null>(null);

  const handleView = async (entry: VersionHistoryEntry) => {
    if (!entry.onView) return;
    setViewEntry(entry);
    setViewContent(null);
    setViewError(null);
    setViewLoading(true);
    try {
      const content = await entry.onView();
      setViewContent(content);
    } catch (err: any) {
      setViewError(err?.response?.data?.message || err.message || 'Failed to load this version.');
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setViewEntry(null);
    setViewContent(null);
    setViewError(null);
  };

  const handleRestoreFromDialog = () => {
    viewEntry?.onRestore?.();
    closeView();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420 } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage ?? 'No prior versions yet.'}
          </Typography>
        )}
        <List disablePadding>
          {entries.map((entry) => (
            <ListItem
              key={entry.key}
              sx={{
                display: 'block',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1.5,
                px: 1.5,
                py: 1,
              }}
            >
              <ListItemText
                primary={new Date(entry.updatedAt).toLocaleString()}
                secondary={
                  <>
                    By {entry.updatedBy || 'unknown'}
                    {entry.summary}
                  </>
                }
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {entry.onView && (
                  <Button size="small" onClick={() => handleView(entry)}>
                    View
                  </Button>
                )}
                {entry.onRestore && (
                  <Tooltip
                    title={entry.restoreDisabledReason ?? ''}
                    disableHoverListener={!entry.restoreDisabledReason}
                  >
                    <span>
                      <Button size="small" disabled={!!entry.restoreDisabledReason} onClick={entry.onRestore}>
                        Restore this version
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </ListItem>
          ))}
        </List>
      </Box>

      <Dialog open={viewEntry !== null} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6">{viewContent?.title ?? 'Version preview'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {viewContent
                  ? `By ${viewContent.updatedBy || 'unknown'} on ${new Date(viewContent.updatedAt).toLocaleString()}`
                  : viewEntry
                    ? `By ${viewEntry.updatedBy || 'unknown'} on ${new Date(viewEntry.updatedAt).toLocaleString()}`
                    : ''}
              </Typography>
            </Box>
            <IconButton onClick={closeView} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          {viewContent && (viewContent.status || (viewContent.tags?.length ?? 0) > 0) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
              {viewContent.status && <Chip size="small" label={viewContent.status} />}
              {(viewContent.tags ?? []).map((tag) => (
                <Chip key={tag} size="small" variant="outlined" label={tag} />
              ))}
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading && <CircularProgress />}
          {viewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {viewError}
            </Alert>
          )}
          {viewContent && !viewLoading && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <SafeMarkdown source={viewContent.body} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewEntry?.onRestore && (
            <Tooltip
              title={viewEntry.restoreDisabledReason ?? ''}
              disableHoverListener={!viewEntry.restoreDisabledReason}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!!viewEntry.restoreDisabledReason}
                  onClick={handleRestoreFromDialog}
                >
                  Restore this version
                </Button>
              </span>
            </Tooltip>
          )}
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
